// src/views/Clientes/DescuentoFlota/CrearDescuentoFlota.jsx
import React, { useEffect, useMemo, useState } from "react";
import BtnGuardar from "../../../components/buttons/BtnGuardar";
import BtnCancelar from "../../../components/buttons/BtnCancelar";
import ClienteAutocomplete from "../../../components/autocomplete/ClienteAutocomplete";
import apiRoutes from "../../../api/apiRoutes";
import { appFetchJson } from "../../../utils/http";
import ProductoMultiSelect from "../../../components/multiselect/ProductoMultiSelect";
import EessMultiSelect from "../../../components/multiselect/EessMultiSelect";
import TarjetasPorClienteMultiSelect from "../../../components/multiselect/TarjetasPorClienteMultiSelect";

/**
 * Crear Descuento Flota
 * Modal estándar
 */
export default function CrearDescuentoFlota({
  onClose = () => {},
  onSaved = () => {},
}) {
  // =========================
  // ESTADO GENERAL
  // =========================
  const [cliente, setCliente] = useState(null);
  const [fechaInicio, setFechaInicio] = useState(getTomorrowISO());
  const [fechaFin, setFechaFin] = useState("");

  const [modo, setModo] = useState("MONTO");

  const [totalMonto, setTotalMonto] = useState("");
  const [montoEess, setMontoEess] = useState("");
  const [montoPetro, setMontoPetro] = useState("");

  const [totalPct, setTotalPct] = useState("");
  const [pctEess, setPctEess] = useState("");
  const [pctPetro, setPctPetro] = useState("");

  const [producto, setProducto] = useState("");
  const [eess, setEess] = useState("");
  const [tarjeta, setTarjeta] = useState("");

  const [productos, setProductos] = useState([]);
  const [eessSel, setEessSel] = useState([]);
  const [tarjetas, setTarjetas] = useState([]);

  const [saving, setSaving] = useState(false);
  const [saveErr, setSaveErr] = useState("");

  // =========================
  // CÁLCULOS AUTOMÁTICOS (MONTO)
  // =========================
  useEffect(() => {
    if (modo !== "MONTO") return;

    const t = Number(totalMonto || 0);
    const e = Number(montoEess || 0);

    // 🔹 si Total Valor se borra o queda en 0 → limpiar relacionados
    if (totalMonto === "" || t === 0) {
      setMontoEess("");
      setMontoPetro("");
      return;
    }

    if (isNaN(t) || isNaN(e)) {
      setMontoPetro("");
      return;
    }

    setMontoPetro(Math.max(0, t - e));
  }, [modo, totalMonto, montoEess]);



  // =========================
  // LIMPIAR TARJETAS AL CAMBIAR CLIENTE
  // =========================
  useEffect(() => {
    setTarjetas([]);
  }, [cliente?.id]);

  // =========================
  // CÁLCULOS AUTOMÁTICOS (PORCENTAJE)
  // =========================
  useEffect(() => {
    if (modo !== "PORCENTAJE") return;

    const t = Number(totalPct || 0);
    const e = Number(pctEess || 0);

    // 🔹 si Total % se borra o queda en 0 → limpiar relacionados
    if (totalPct === "" || t === 0) {
      setPctEess("");
      setPctPetro("");
      return;
    }

    if (isNaN(t) || isNaN(e)) {
      setPctPetro("");
      return;
    }

    setPctPetro(Math.max(0, 100 - e));
  }, [modo, totalPct, pctEess]);


  // =========================
  // VALIDACIÓN
  // =========================
  const isValid = useMemo(() => {
    if (!cliente) return false;
    if (!fechaInicio) return false;
    return modo === "MONTO"
      ? Number(totalMonto) > 0
      : Number(totalPct) > 0;
  }, [cliente, fechaInicio, modo, totalMonto, totalPct]);

  // =========================
  // ESC PARA CERRAR
  // =========================
  useEffect(() => {
    const esc = (e) => e.key === "Escape" && onClose();
    window.addEventListener("keydown", esc);
    return () => window.removeEventListener("keydown", esc);
  }, [onClose]);

  // =========================
  // GUARDAR
  // =========================
  const handleGuardar = async () => {
    if (!isValid || saving) return;

    setSaving(true);
    setSaveErr("");

    try {
      const payload = {
        // =====================
        // CLIENTE
        // =====================
        clienteId: cliente.id,

        // =====================
        // FECHAS
        // =====================
        fechaInicio,
        fechaFin: fechaFin || null,

        // =====================
        // VALORES (según modo)
        // =====================
        ...(modo === "MONTO"
          ? {
              valorDescuento: Number(totalMonto),
              valorEess: Number(montoEess || 0),
              valorPetr: Number(montoPetro || 0),
            }
          : {
              prDescuento: Number(totalPct),
              prEess: Number(pctEess || 0),
              prPetr: Number(pctPetro || 0),
            }),

        // =====================
        // LISTAS (MULTISELECT)
        // =====================
        productoIds: productos,
        eessIds: eessSel,
        tarjetaIds: tarjetas,

      };

      await appFetchJson(
        apiRoutes.descuentoFlota.create,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify(payload),
        },
        {
          showSuccess: true,
          successMessage: "Descuento creado correctamente",
          onSuccessClose: () => onSaved(),
        }
      );
    } catch (e) {
      console.error(e);
      setSaveErr(e?.message || "Error al crear el descuento.");
    } finally {
      setSaving(false);
    }
  };


  // =========================
  // RENDER
  // =========================
  return (
    <div className="modal-backdrop" onClick={onClose}>
      <div className="modal-panel-lg" onClick={(e) => e.stopPropagation()}>
        {/* HEADER */}
        <div className="modal-header flex justify-between items-center">
          <h2 className="section-title">
            Crear Descuento Flota - Rellene el Formulario
          </h2>
          <span className="text-sm text-red-600">(*) Campos obligatorios</span>
        </div>

        {/* BODY */}
        <div className="modal-body">
          {saveErr && <div className="mb-3 text-red-600">{saveErr}</div>}

          <div className="border rounded-lg bg-white">
            <div className="px-6 py-6 space-y-1">
              <FormRow label="Cliente:" required>
                <ClienteAutocomplete
                  value={cliente}
                  onChange={setCliente}
                  placeholder="Buscar cliente..."
                />
              </FormRow>

              <FormRow label="Fecha inicio:" required>
                <input
                  type="date"
                  className="input"
                  value={fechaInicio}
                  min={getTomorrowISO()}   // 👈 esto bloquea fechas menores
                  onChange={(e) => setFechaInicio(e.target.value)}
                />
              </FormRow>

              <FormRow label="Fecha fin:" required>
                <input
                  type="date"
                  className="input"
                  value={fechaFin}
                  min={getTomorrowISO()}   // 👈 esto bloquea fechas menores
                  onChange={(e) => setFechaFin(e.target.value)}
                />
              </FormRow>

              {/* PESTAÑAS */}
              <div className="border-b mb-4 flex gap-6 px-2">
                <Tab
                  active={modo === "MONTO"}
                  onClick={() => {
                    setModo("MONTO");
                    setTotalPct("");
                    setPctEess("");
                    setPctPetro("");
                  }}
                >
                  Valores en montos
                </Tab>
                <Tab
                  active={modo === "PORCENTAJE"}
                  onClick={() => {
                    setModo("PORCENTAJE");
                    setTotalMonto("");
                    setMontoEess("");
                    setMontoPetro("");
                  }}
                >
                  Valores en porcentajes
                </Tab>
              </div>

              {modo === "MONTO" ? (
                <div className="space-y-1 bg-gray-50 p-4 rounded">
                  <InputRow
                    label="Total Descuento Valor PYG"
                    value={totalMonto}
                    onChange={setTotalMonto}
                  />
                  <InputRow
                    label="Valor PYG EE.SS."
                    value={montoEess}
                    onChange={setMontoEess}
                  />
                  <TextRow
                    label="Valor PYG Petrobras"
                    value={formatNumber(montoPetro)}
                  />
                </div>
              ) : (
                <div className="space-y-1 bg-gray-50 p-4 rounded">
                  <InputRow
                    label="Total Descuento %"
                    value={totalPct}
                    onChange={(v) => setTotalPct(v.replace(",", "."))}
                  />
                  <InputRow
                    label="EE.SS. %"
                    value={pctEess}
                    onChange={(v) => setPctEess(v.replace(",", "."))}
                  />
                  <TextRow
                    label="Petrobras %"
                    value={formatNumber(pctPetro)}
                  />
                </div>
              )}

              {/* PRODUCTO / EE.SS. / TARJETA */}
              <div className="border rounded p-4 space-y-1">
                <p className="font-semibold mb-3">
                  Cargue aquí los descuentos por Producto, EE.SS. y Tarjeta
                </p>

                <FormRow label="Producto:">
                  <ProductoMultiSelect
                    value={productos}
                    onChange={setProductos}
                  />
                </FormRow>

                <FormRow label="EE.SS.:">
                  <EessMultiSelect value={eessSel} onChange={setEessSel} />
                </FormRow>

                <FormRow label="Tarjeta:">
                  <TarjetasPorClienteMultiSelect
                    clienteId={cliente?.id}
                    value={tarjetas}
                    onChange={setTarjetas}
                  />
                </FormRow>
              </div>
            </div>
          </div>
        </div>

        {/* FOOTER */}
        <div className="modal-footer">
          <div className="btn-row">
            <BtnGuardar
              isValid={isValid}
              saving={saving}
              onClick={handleGuardar}
            />
            <BtnCancelar onClick={onClose} />
          </div>
        </div>
      </div>
    </div>
  );
}

function getTomorrowISO() {
  const d = new Date();
  d.setDate(d.getDate() + 1);
  return d.toISOString().split("T")[0]; // YYYY-MM-DD
}

/* =========================
   COMPONENTES AUX
========================= */

function formatNumber(v) {
  if (v === null || v === undefined || v === "") return "—";
  return String(v).replace(",", ".");
}

function FormRow({ label, required, children }) {
  return (
    <div className="grid grid-cols-[160px_1fr] items-center gap-3 max-w-2xl">
      <label className="text-right">
        {label}
        {required && <span className="text-red-600 ml-1">*</span>}
      </label>
      {children}
    </div>
  );
}

function InputRow({ label, value, onChange }) {
  return (
    <div className="grid grid-cols-[200px_1fr] items-center gap-3 max-w-xl">
      <label className="text-right">{label}:</label>
      <input
        type="number"
        className="input"
        value={value}
        onChange={(e) => onChange(e.target.value)}
      />
    </div>
  );
}

function TextRow({ label, value }) {
  return (
    <div className="grid grid-cols-[200px_1fr] items-center gap-3 max-w-xl">
      <label className="text-right">{label}:</label>
      <div className="py-2 font-medium text-gray-800">{value}</div>
    </div>
  );
}

function Tab({ active, onClick, children }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`pb-2 ${
        active
          ? "border-b-2 border-green-600 text-green-700 font-semibold"
          : "text-gray-600"
      }`}
    >
      {children}
    </button>
  );
}
